/**
 * Robustly copies text to the clipboard.
 * Supports both modern navigator.clipboard API and legacy document.execCommand('copy').
 * This is necessary because navigator.clipboard is only available in secure contexts (HTTPS).
 * 
 * @param {string} text - The text to copy to the clipboard.
 * @returns {Promise<boolean>} - Resolves to true if the copy was successful, false otherwise.
 */
export const copyToClipboard = async (text) => {
    // 1. Try modern API first (requires secure context/HTTPS)
    if (navigator.clipboard && window.isSecureContext) {
        try {
            await navigator.clipboard.writeText(text);
            return true;
        } catch (err) {
            console.error('navigator.clipboard.writeText failed, trying fallback:', err);
        }
    }

    // 2. Fallback to legacy document.execCommand('copy')
    try {
        const textArea = document.createElement("textarea");
        textArea.value = text;
        
        // Ensure the textarea is off-screen and not visible
        textArea.style.position = "fixed";
        textArea.style.left = "-9999px";
        textArea.style.top = "0";
        textArea.style.opacity = "0";
        textArea.style.pointerEvents = "none";
        
        document.body.appendChild(textArea);
        
        textArea.focus();
        textArea.select();
        
        const successful = document.execCommand('copy');
        document.body.removeChild(textArea);
        
        if (successful) {
            return true;
        } else {
            throw new Error('execCommand copy returned false');
        }
    } catch (err) {
        console.error('Clipboard fallback failed:', err);
        return false;
    }
};
